import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { type SceneCategory, type SceneGroup, VideoData } from '@lumen5/luminary';
import { z } from 'zod';

import type { ToolExecutionContext } from '../types';

import { describeCoveredWords, resolveOverlayRangeOperation } from './overlay-placement';
import { isMotionGraphicsSceneRequested } from './motion-graphics/motionGraphicsSceneDesign';
import { computeSceneShortIds } from './scene-addressing';
import { resolveSceneGroupById, resolveSceneGroupHandle } from './scene-group-addressing';
import { validateSceneDesignId } from './scene-design-validation';
import { getInterleavedTranscript } from './transcript-tokens';
import { VideoStateTool } from './VideoStateTool';

import { getTemplateDefaultCutawaySceneDesign } from 'js/src/data/templates';
import {
    getTemplateDefaultTalkingHeadSceneDesign,
    getTemplateDefaultVoiceoverSceneDesign,
} from 'js/src/data/templates/templates-selectors';
import { checkpointVideo } from 'js/src/data/video-data';
import { refreshFirstScenePreviewOperation } from 'js/src/data/video-planner';
import type { RootState } from 'js/src/libs/RootState';
import { saveRawLuminary } from 'js/src/luminary';
import { createCodeoOverlayAtTranscriptRangeOperation } from 'js/src/luminary/html-asset-operations';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import { moveOtherOverlaysAndCreateOverlayOperation } from 'js/src/luminary/luminary-operations';
import {
    isAIAvatarSceneGroup,
    isOverlaySceneDesign,
    isVoiceoverSceneGroup,
} from 'js/src/luminary/luminary-selectors';

export const addVoiceoverSceneSchema = z.object({
    scene_group_id: z
        .string()
        .describe(
            'The stable scene_group_id of the scene group to add the scene to (from the video state).',
        ),
    scenes: z
        .array(
            z.object({
                words: z
                    .array(z.string())
                    .min(1)
                    .describe(
                        'Array of consecutive tokens from the transcript this scene should cover. These must appear sequentially in the transcript exactly as spelled, including any `<pause seconds="N"/>` token that falls between them (write each pause exactly as it appears in the transcript).',
                    ),
                scene_design_id: z
                    .string()
                    .describe(
                        'The ID of the scene design to use for this scene (e.g., "minimal:text_1")',
                    ),
            }),
        )
        .min(1)
        .describe(
            'The scenes to add. Each covers its own non-overlapping range of the transcript; add every scene for this scene group in one call rather than one call per scene.',
        ),
});
export type AddVoiceoverSceneArgs = z.infer<typeof addVoiceoverSceneSchema>;

export class AddVoiceoverSceneTool extends VideoStateTool<typeof addVoiceoverSceneSchema> {
    protected override readonly schema = addVoiceoverSceneSchema;
    protected override readonly toolName = TOOL_NAMES.addVoiceoverScene;
    protected override readonly toolDescription = `Add a new scene to a voiceover or talking-head scene group, over specific sequences of transcript words. A range starting at the first transcript token also covers the leading silence before it, and one ending at the last token runs to the end — so a full-video treatment (e.g. a waveform/audiogram design) is created by passing every token of the transcript. Use ${TOOL_NAMES.listSceneDesigns} first to find a suitable design (e.g. a lower third), passing narrative_thread="talking_head" for a talking-head scene group or narrative_thread="voiceover" for a voiceover scene group. Choose ${TOOL_NAMES.addVoiceoverScene} when the audio structure should stay the same, the user wants emphasis on specific words, or you're adding a visual moment during speech. (To create a structural break in the narration instead, use ${TOOL_NAMES.splitSceneGroup}.)`;

    public override getToolDisplayName(toolCallArgs?: string): string {
        if (toolCallArgs) {
            try {
                const parsed = JSON.parse(toolCallArgs) as Partial<AddVoiceoverSceneArgs>;
                if ((parsed.scenes?.length ?? 0) > 1) {
                    return `Adding ${parsed.scenes!.length} voiceover scenes`;
                }
            } catch {
                // fall through to the singular name
            }
        }
        return 'Adding voiceover scene';
    }

    protected override onEditApplied(args: AddVoiceoverSceneArgs): void {
        // A scene added to the first scene group renders over the first scene, so it changes the
        // thumbnail. Resolve defensively — this runs after a successful edit, so it must never throw.
        const resolved = resolveSceneGroupById(this.luminary, args.scene_group_id);
        if (!('error' in resolved) && resolved.index === 0) {
            this.dispatch(refreshFirstScenePreviewOperation());
        }
    }

    public get statusIcon(): string {
        return 'layers';
    }

    /**
     * The motion-graphics branch: lay an empty graphic over the resolved words, built on the
     * template's default cutaway and then cleared. The design tool authors it afterwards.
     */
    private addMotionGraphicsScene(
        sceneGroupId: string,
        startTimeMillis: number,
        endTimeMillis: number,
        templateId: string,
    ): { sceneId: string } | { error: string } {
        const cutawaySceneDesignId = getTemplateDefaultCutawaySceneDesign(
            this.getState(),
            templateId,
        );
        if (!cutawaySceneDesignId) {
            return {
                error: `template "${templateId}" has no default cutaway scene design, so a motion graphics scene can't be added to a voice-driven scene group.`,
            };
        }

        const { createCodeoOverlayAtTranscriptRange } = makeOperationsCallable(
            { createCodeoOverlayAtTranscriptRangeOperation },
            this.luminary,
            this.dispatch,
            this.getState as () => RootState,
        );
        const sceneIdsBefore = this.snapshotSceneIds();
        const created = createCodeoOverlayAtTranscriptRange(
            sceneGroupId,
            startTimeMillis,
            endTimeMillis,
            cutawaySceneDesignId,
        );
        if (!created) {
            return {
                error: `could not add a motion graphics scene to scene group ${sceneGroupId}.`,
            };
        }

        this.startMotionGraphicsSceneBuilding(created.sceneId);
        this.blurScenesCreatedSince(sceneIdsBefore, [created.sceneId]);

        return { sceneId: created.sceneId };
    }

    /**
     * Validate and place one requested scene. Returns the new scene's id, or the reason it was
     * refused so the batch can report it without abandoning the scenes that did land.
     */
    private addOneScene(
        sceneGroup: SceneGroup,
        words: string[],
        sceneDesignId: string,
        parentActionGroup: string,
    ): { sceneId: string; isMotionGraphics: boolean } | { error: string } {
        const state = this.getState();
        const currentTemplateId = this.luminary.template.id;

        // A motion graphics scene brings no template design to vet — it is created from the
        // default cutaway and then cleared — so the design-compatibility rules below don't apply.
        const isMotionGraphicsScene = isMotionGraphicsSceneRequested(sceneDesignId);
        if (!isMotionGraphicsScene) {
            const validation = validateSceneDesignId(this.luminary, sceneDesignId);
            if (validation.error !== undefined) {
                return { error: validation.error.replace(/^Error:\s*/, '') };
            }

            // Added scenes inherit the design's category, and only voice-driven compatible
            // categories are recognized as scenes that can be added this way (and accepted
            // as base scenes by future scene creation), so reject incompatible designs
            // upfront — same rule the OverlaySelector UI applies when offering these designs.
            const designCategory: SceneCategory = validation.sceneCategory;

            const voiceDrivenDefaultSceneDesignIds = {
                defaultTalkingHeadSceneDesignId: getTemplateDefaultTalkingHeadSceneDesign(
                    state,
                    currentTemplateId,
                ),
                defaultVoiceoverSceneDesignId: getTemplateDefaultVoiceoverSceneDesign(
                    state,
                    currentTemplateId,
                ),
            };
            if (
                !isOverlaySceneDesign(
                    designCategory,
                    sceneDesignId,
                    voiceDrivenDefaultSceneDesignIds,
                )
            ) {
                const isBaseDesign =
                    sceneDesignId ===
                        voiceDrivenDefaultSceneDesignIds.defaultTalkingHeadSceneDesignId ||
                    sceneDesignId ===
                        voiceDrivenDefaultSceneDesignIds.defaultVoiceoverSceneDesignId;
                return {
                    error: isBaseDesign
                        ? `Scene design "${sceneDesignId}" is the base scene design of voice-driven scene groups and cannot be used for an added scene. Use ${TOOL_NAMES.listSceneDesigns} and choose a different design.`
                        : `Scene design "${sceneDesignId}" (category "${designCategory}") cannot be used for a scene added to a voice-driven scene group. Use ${TOOL_NAMES.listSceneDesigns} and choose a design with a compatible category.`,
                };
            }
            if (
                isVoiceoverSceneGroup(sceneGroup) &&
                !isAIAvatarSceneGroup(sceneGroup) &&
                designCategory === VideoData.CATEGORY_TALKING_HEAD_INSERT
            ) {
                return {
                    error: `Scene design "${sceneDesignId}" is a talking-head insert design and cannot be used for a scene added to a voiceover scene group. Use ${TOOL_NAMES.listSceneDesigns} and choose a different design.`,
                };
            }
        }

        const { resolveOverlayRange } = makeOperationsCallable(
            { resolveOverlayRangeOperation },
            this.luminary,
            this.dispatch,
            this.getState as () => RootState,
        );
        const range = resolveOverlayRange(sceneGroup, words);
        if ('error' in range) {
            return { error: range.error };
        }
        const { startTimeMillis, endTimeMillis } = range;

        if (isMotionGraphicsScene) {
            const created = this.addMotionGraphicsScene(
                sceneGroup.id,
                startTimeMillis,
                endTimeMillis,
                currentTemplateId,
            );
            return 'error' in created ? created : { ...created, isMotionGraphics: true };
        }

        const { moveOtherOverlaysAndCreateOverlay } = makeOperationsCallable(
            { moveOtherOverlaysAndCreateOverlayOperation },
            this.luminary,
            this.dispatch,
            this.getState as () => RootState,
        );

        const sceneIdsBefore = this.snapshotSceneIds();
        const newSceneId = moveOtherOverlaysAndCreateOverlay(
            sceneGroup.id,
            startTimeMillis,
            endTimeMillis,
            sceneDesignId,
            false, // systemTriggered
            parentActionGroup,
        );

        // Mark the new scene as "building" so the build preview blurs
        // it until the content it needs lands (set_text/set_media/image_ask).
        this.startSceneBuilding(newSceneId);
        // Creating the overlay also splits the base scene underneath it, so the
        // remainder of the scene group is a new base scene that needs blurring too.
        this.blurScenesCreatedSince(sceneIdsBefore, [newSceneId]);

        return { sceneId: newSceneId, isMotionGraphics: false };
    }

    public async execute(args: AddVoiceoverSceneArgs, context: ToolExecutionContext) {
        const { scenes } = args;
        const { parentActionGroup } = context;

        const { sceneGroup } = resolveSceneGroupHandle(this.luminary, args);

        // Check if scene group has a transcript. transcriptTokens is the canonical interleaved
        // (word + pause) list the agent reads, so the words it passes back match token-for-token.
        const transcript = sceneGroup.originalTranscript;
        const transcriptTokens = getInterleavedTranscript(this.getState(), sceneGroup);

        if (!transcript || transcriptTokens.length === 0) {
            return `Error: Scene group ${sceneGroup.id} does not have a transcript. Only voiceover and talking-head scene groups with transcripts can have scenes added to them.`;
        }

        const added: {
            sceneId: string;
            isMotionGraphics: boolean;
            requestIndex: number;
            words: string[];
        }[] = [];
        const failures: string[] = [];

        scenes.forEach(({ words, scene_design_id }, requestIndex) => {
            const describeRequest = `scenes[${requestIndex}] ("${scene_design_id}")`;
            try {
                const result = this.addOneScene(
                    sceneGroup,
                    words,
                    scene_design_id,
                    parentActionGroup,
                );
                if ('error' in result) {
                    failures.push(`${describeRequest}: ${result.error}`);
                    return;
                }
                added.push({ ...result, requestIndex, words });
            } catch (error) {
                const errorMessage =
                    error instanceof Error ? error.message : 'Unknown error occurred';
                failures.push(`${describeRequest}: ${errorMessage}`);
            }
        });

        // One checkpoint for the whole batch — the scenes were added together and are undone together.
        if (added.length > 0) {
            this.dispatch(
                checkpointVideo({
                    video_data: saveRawLuminary(this.luminary),
                    systemTriggered: false,
                    parentActionGroup,
                }),
            );
        }

        return this.describeOutcome(added, failures);
    }

    /**
     * Report the batch back to the agent: every added scene by the scene_id the edit tools address
     * it by, plus any request that was refused, named so a retry can target just that one.
     */
    private describeOutcome(
        added: {
            sceneId: string;
            isMotionGraphics: boolean;
            requestIndex: number;
            words: string[];
        }[],
        failures: string[],
    ): string {
        if (added.length === 0) {
            return `Error adding scene: ${failures.join('; ')}`;
        }

        const shortIds = computeSceneShortIds(this.luminary.scenes.map((scene) => scene.id));
        const shortIdOf = (sceneId: string) => shortIds.get(sceneId) ?? sceneId;

        // A single scene names the words it covers, as it always has. A batch identifies each
        // scene by the request slot it came from instead — echoing every range back would replay
        // most of the transcript once a batch gets large.
        const summary =
            added.length === 1
                ? // A motion graphics scene is authored by its own follow-up tool, which is what the
                  // caller acts on next, so it is named by id alone rather than by the words it covers.
                  added[0].isMotionGraphics
                    ? `Success: Added an empty motion graphics scene (scene_id: ${shortIdOf(added[0].sceneId)}). It inherits the narration it covers, and the scene underneath stays as the base.`
                    : `Success: Added scene covering ${describeCoveredWords(added[0].words)} (scene_id: ${shortIdOf(added[0].sceneId)}).`
                : `Success: Added ${added.length} scenes:\n` +
                  added
                      .map(
                          (scene) =>
                              `  - scenes[${scene.requestIndex}] → scene_id ${shortIdOf(scene.sceneId)}`,
                      )
                      .join('\n');

        const motionGraphicsShortIds = added
            .filter((scene) => scene.isMotionGraphics)
            .map((scene) => shortIdOf(scene.sceneId));
        const motionGraphicsFollowUp =
            motionGraphicsShortIds.length > 0
                ? `\n\nThe motion graphics scene(s) (${motionGraphicsShortIds.join(', ')}) show nothing yet — ` +
                  `you author each one: ${TOOL_NAMES.writePlan} then ` +
                  `${TOOL_NAMES.writeHtmlAsset}, per scene_id.`
                : '';
        // A motion graphics scene is authored with the design tools above, and set_text/set_media
        // cannot edit one — so an all-motion-graphics batch is not sent to them.
        const hasDesignedScenes = added.some((scene) => !scene.isMotionGraphics);
        const contentFollowUp = hasDesignedScenes
            ? `\n\nSet each scene's text/media with ${TOOL_NAMES.setText}/${TOOL_NAMES.setMedia} addressing it by scene_id. Match the content to the words it covers.`
            : '';
        const failureReport =
            failures.length > 0
                ? `\n\nNot added (fix and retry just these): ${failures.join('; ')}`
                : '';

        return `${summary}${motionGraphicsFollowUp}${contentFollowUp}${failureReport}`;
    }
}
